import React, { createContext, useState, useEffect } from 'react';
import { translateKey } from '../localization/Translations';
import { showError } from '../utils/utils';
import literalsRequest from '../../services/literals.service';
import { useRouter } from 'next/router';
import chainLanguagesRequest from '../../services/chainLanguages.service';
import hotelLanguagesRequest from '../../services/hotelLanguages.service';
import { ConfigProvider } from 'antd';

import ca from 'antd/lib/locale/ca_ES';
import de from 'antd/lib/locale/de_DE';
import en from 'antd/lib/locale/en_US';
import es from 'antd/lib/locale/es_ES';
import fr from 'antd/lib/locale/fr_FR';
import it from 'antd/lib/locale/it_IT';
import ja from 'antd/lib/locale/ja_JP';
import pt from 'antd/lib/locale/pt_PT';
import pt_BR from 'antd/lib/locale/pt_BR';
import zh from 'antd/lib/locale/zh_CN';
import ru from 'antd/lib/locale/ru_RU';

const antLocales = {
  ca,
  de,
  en,
  es,
  fr,
  it,
  ja,
  pt,
  pt_BR,
  zh,
  ru,
};

export const LanguageContext = createContext({});

const defaultState = {
  literals: null,
  selectedLanguage: 'es',
  firstLoad: true
};

const sessionStorageKey = 'language';
const literalsCache = {};

export function LanguageContextProvider({ children }) {
  const router = useRouter();
  const [state, setState] = useState(defaultState);
  const actions = {
    translate: key => {
      return translateKey(key, state.literals);
    },
    getLiterals: () => {
      if (literalsCache[state.selectedLanguage]) {
        updateState({ literals: literalsCache[state.selectedLanguage] });
      } else {
        literalsRequest(state.selectedLanguage)
          .then(response => {
            literalsCache[state.selectedLanguage] = response;
            updateState({ literals: response });
          })
          .catch(error => {
            showError(error);
          });
      }
    },
    getLanguages: (service, id) => {
      service(id).then(({ languages }) => {
        const newState = { languages };
        if (
          state.firstLoad ||
          !languages.some(language => language.lang === state.selectedLanguage)
        ) {
          newState.firstLoad = false;
          const browserLang = languages.find(language =>
            navigator.language.startsWith(language.lang)
          );
          if (browserLang) {
            newState.selectedLanguage = browserLang.lang;
          } else {
            newState.selectedLanguage = languages[0].lang;
          }
        }
        updateState(newState);
      });
    },
    setLanguage: language => {
      updateState({ selectedLanguage: language });
    },
  };

  useEffect(() => {
    actions.getLiterals();
  }, [state.selectedLanguage]);

  useEffect(() => {
    const persistedLanguageContext = sessionStorage.getItem(sessionStorageKey);
    if (persistedLanguageContext && JSON.parse(persistedLanguageContext)?.languages) {
      updateState(JSON.parse(persistedLanguageContext));
    } else {
      if (router.query.chainId) {
        actions.getLanguages(chainLanguagesRequest, router.query.chainId);
      } else if (router.query.hotelId) {
        actions.getLanguages(hotelLanguagesRequest, router.query.hotelId);
      }
    }
  }, [router.query.chainId, router.query.hotelId]);

  useEffect(() => {
    if (state.languages && router.query.lang) {
      const language = state.languages.find(language => language.lang == router.query.lang);
      if (language) {
        // updateState({ selectedLanguage: language.lang });
        actions.setLanguage(language.lang);
      }
    }
  }, [router.query.lang, state.languages]);

  function updateState(newState) {
    setState(previousState => {
      const finalState = { ...previousState, ...newState };
      sessionStorage.setItem(sessionStorageKey, JSON.stringify(finalState));
      return finalState;
    });
  }

  return (
    <LanguageContext.Provider value={{ state, actions }}>
      <ConfigProvider locale={antLocales[state.selectedLanguage] || es}>{children}</ConfigProvider>
    </LanguageContext.Provider>
  );
}
